import * as modal from "../util/modal.ts";
import * as Html from "../util/Html.ts";
import ApiServer from "../ApiServer.ts";
import {SyncCache} from "../lib/SyncCache.ts";
import * as DateUtils from "../lib/DateUtils.ts";
import {EventStore} from "../server/EventStore.ts";
import * as amplify from "../util/amplify.ts";
import {register} from "../widget/action-menu-registry.ts";
import Icons from "../svg/Icons.ts";
import * as i18n from "../i18n.ts";
import TPL_LIST from "./manageimport_list.mustache";
import TPL_DETAIL_EVENTS from "./manageimport_detail_events.mustache";

const eventImportCache = new SyncCache<ApiServer.types.ExternalImportBean>(
  ApiServer.metasync.prepare("externalImport"),
  function(a,b){ return (b.creationDate?.getTime()||0) - (a.creationDate?.getTime()||0) }
);

let currentFilter = 'all';
let eventCountByImportId: Map<string, number> = new Map();
let cachedEvents: ApiServer.types.IEvent[] = [];

async function buildEventCounts(){
  cachedEvents = await EventStore.getFor(null);
  var counts = new Map<string, number>();
  cachedEvents.forEach(ev=>{
    if(ev.externalImportId){
      counts.set(ev.externalImportId, (counts.get(ev.externalImportId) || 0) + 1);
    }
  });
  eventCountByImportId = counts;
}

const importTypeEmoji = {
  'EVENT_BY_INBOX': '📧',
  'EVENT_BY_URL': '🔗',
  'EVENT_BY_ICAL': '📎'
};
const importTypeSvg = {
  'EVENT_BY_INBOX': 'ic_email',
  'EVENT_BY_URL': 'ic_link',
  'EVENT_BY_ICAL': 'ic_attachment'
};

function formatImports(imports: ApiServer.types.ExternalImportBean[]){
  imports = imports.filter(item=>eventCountByImportId.get(item.metaId)>0)
  const filtered = currentFilter === 'all' ? imports : imports.filter(i=>i.importType === currentFilter);
  return filtered.map(item=>{
    return {
      metaId: item.metaId,
      importType: item.importType,
      emoji: importTypeEmoji[item.importType] || '📅',
      svg: importTypeSvg[item.importType] || 'ic_email',
      eventCount: eventCountByImportId.get(item.metaId) || 0,
      info: item.info || '',
      date: item.creationDate ? DateUtils.format(item.creationDate) : ''
    }
  })
}

function refreshList(){
  var list = document.querySelector('.manageimport-list') as HTMLElement;
  if(list) Html.setHtml(list, TPL_LIST, {items: formatImports(eventImportCache.datas || [])});
}


Html.registerDom({
  async manageimport(){
    currentFilter = 'all';
    var [imports] = await Promise.all([eventImportCache.need(), buildEventCounts()]);
    var domModal = modal.openDomModal(await import('./manageimport.mustache'),{
      items: formatImports(imports)
    },{
      async eventImportUpdated(){
        await eventImportCache.need();
        await buildEventCounts();
        refreshList();
      }
    }), pane = domModal.querySelector('.pane-right')
    setTimeout(()=>pane?.classList.add('open'),33)
  },
  async openImportDetail(importId: string){
    var item = eventImportCache.get(importId);
    if(!item) return;

    var matchingEvents = cachedEvents.filter(ev=>ev.externalImportId === importId);

    var domModal = modal.openDomModal(await import('./manageimport_detail.mustache'),{
      importId,
      info: item.info || '',
      date: item.creationDate ? DateUtils.format(item.creationDate) : '',
      eventCount: matchingEvents.length,
      events: matchingEvents.slice(0,50).map(ev=>({
        metaId: ev.metaId,
        text: ev.text || '',
        date: ev.startDate ? DateUtils.format(ev.startDate) : '',
        color: ev.colorUI
      }))
    },{
      async eventDeleted(){
        EventStore.invalidate();
        await refreshDetail();
        amplify.publish("eventImportUpdated");
      },
      async eventImportUpdated(){
        await refreshDetail();
      }
    }), pane = domModal.querySelector('.pane-right')

    async function refreshDetail(){
      await buildEventCounts();
      var remaining = cachedEvents.filter(ev=>ev.externalImportId === importId);
      if(remaining.length === 0){
        modal.closeDomModal(domModal);
      }else{
        Html.setHtml(
          domModal.querySelector('.manageimport-detail-events') as HTMLElement,
          TPL_DETAIL_EVENTS,
          {events: remaining.slice(0,50).map(ev=>({metaId:ev.metaId, text:ev.text||'', date:ev.startDate ? DateUtils.format(ev.startDate) : '', color:ev.colorUI}))}
        );
        var countEl = domModal.querySelector('.manageimport-detail-count strong');
        if(countEl) countEl.textContent = remaining.length + countEl.textContent.replace(/^\d+/,'');
      }
    }
    setTimeout(()=>pane?.classList.add('open'), 33);
  },
  async manageimportdelete(importId: string){
    var confirmed = await modal.confirmDelete("manageimport.detail.confirmdelete");
    if(!confirmed) return;
    await ApiServer.evtexternalimportdelete(true, [importId]);
    modal.closeDomModal();
    modal.toastDone('manageimport.detail.toastdeleted');
    eventImportCache.invalidate();
    EventStore.invalidate();
    amplify.publish("eventImportUpdated");
  },
  async manageimportfilter(value){
    currentFilter = value;
    await eventImportCache.need();

    var list = document.querySelector('.manageimport-list') as HTMLElement;
    if(!list) return;

    var pane = list.closest('.pane-right');
    var chips = pane?.querySelectorAll('.rounded-corner-chips');
    chips?.forEach(chip=>{
      var onclick = chip.getAttribute('data-fw-onclick');
      chip.classList.toggle('selected', onclick === 'manageimportfilter|'+value);
    });

    refreshList();
  }
})

register({
  manageimportbatch(){
    return {
      menu:[
        {
          svg: Icons.ic_member_select,
          i18nkey: 'manageimport.batch.assign',
          onclick: 'batchassign'
        },
        {
          svg: Icons.ic_palette,
          i18nkey: 'manageimport.batch.color',
          onclick: 'batchcolor'
        }
      ]
    }
  }
});

Html.registerDom({
  async batchassign(){
    var importId = Html.getDomMetaId(this);
    var result = await modal.pickAssignee(i18n.get('manageimport.batch.assign'));
    if(!result) return;
    var events = cachedEvents.filter(ev=>ev.externalImportId === importId);
    var metaIds = events.map(ev=>ev.metaId);
    var attendee = result.accountIds.map(id=>({accountId:id, status:'ACCEPTED'}));
    var count = await ApiServer.evtbatchupdate(metaIds, new ApiServer.types.EventInputBean({attendee, isToAll:result.isToAll} as any));
    modal.toastDone('manageimport.batch.done', count);
    EventStore.invalidate();
    amplify.publish("eventImportUpdated");
  },
  async batchcolor(){
    var importId = Html.getDomMetaId(this);
    var color = await modal.pickColor(i18n.get('manageimport.batch.color'));
    if(!color) return;
    var events = cachedEvents.filter(ev=>ev.externalImportId === importId);
    var metaIds = events.map(ev=>ev.metaId);
    var count = await ApiServer.evtbatchupdate(metaIds, new ApiServer.types.EventInputBean({color}));
    modal.toastDone('manageimport.batch.done', count);
    EventStore.invalidate();
    amplify.publish("eventImportUpdated");
  }
});

export { eventImportCache as importCache }
